/**
 * Types and copy for provider payment reconciliation (SPEC-invoice-merge-history R5 iteration 4).
 *
 * Held card payments (ADV-R5-04) and checkout reservations with an unconfirmed outcome (ADV-R5-05).
 * The shapes mirror `routers/invoice_payment_reconciliation.py`; nothing here guesses a server value.
 */

export interface HeldPaymentLegacyReceipt {
  payment_id: string;
  amount: string;
  paid_at: string | null;
  status: string;
  recorded_by: string | null;
}

export interface HeldPayment {
  hold_id: string;
  /** Echoed back on resolve: a hold whose facts moved since is refused (409) so the reviewer looks again. */
  version: string;
  status: 'open' | 'resolved';
  provider_payment_intent_id: string;
  amount_cents: number;
  amount: string;
  invoice_number: string | null;
  owner_invoice_id: string;
  legacy_payments: HeldPaymentLegacyReceipt[];
  created_at: string | null;
}

export type HoldOutcome = 'post_as_new_payment' | 'confirm_duplicate_of_legacy_receipt';

export const HOLD_OUTCOME_LABELS: Record<HoldOutcome, string> = {
  post_as_new_payment: 'It is a new payment: add it to this invoice',
  confirm_duplicate_of_legacy_receipt: 'It is the same payment as the earlier receipt: add nothing',
};

/** A reservation that blocks a merge or a payer change (the server's `reservations` detail). */
export interface BlockingReservation {
  reservation_id: string;
  invoice_id: string;
  requested_invoice_id?: string;
  state: string;
  reconciliation_required?: boolean;
  created_at?: string | null;
  expires_at?: string | null;
}

export type ReconcileOutcomeCode =
  | 'cleared_no_intent'
  | 'payment_posted'
  | 'intent_cancelled'
  | 'already_resolved'
  | 'still_blocked';

export interface ReconcileResult {
  outcome: ReconcileOutcomeCode;
  reservation_state: string;
  message: string;
  can_cancel?: boolean;
  provider_payment_intent_id?: string | null;
}

/** The reservation no longer blocks: the caller may review again. */
export const isSettled = (outcome: ReconcileOutcomeCode): boolean => outcome !== 'still_blocked';

export function parseBlockingReservations(value: unknown): BlockingReservation[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (row): row is BlockingReservation =>
      !!row && typeof row === 'object' && typeof (row as BlockingReservation).reservation_id === 'string'
      && typeof (row as BlockingReservation).invoice_id === 'string',
  );
}

export const formatHeldAmount = (amount: string): string => {
  const n = Number(amount);
  return Number.isFinite(n) ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : amount;
};
